import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CLINIC } from "@/lib/brand";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import {
  LayoutDashboard, Users, CalendarDays, FilePen,
  Receipt, Megaphone, Sparkles, ShieldCheck, KeyRound, LogOut
} from "lucide-react";

const NAV = [
  { to: "/dashboard",    icon: LayoutDashboard, label: "Overview" },
  { to: "/patients",     icon: Users,          label: "Patients" },
  { to: "/appointments", icon: CalendarDays,   label: "Appointments" },
  { to: "/prescriptions",icon: FilePen,        label: "Prescriptions", roles: ["admin","doctor"] },
  { to: "/billing",      icon: Receipt,        label: "Billing" },
  { to: "/leads",        icon: Megaphone,      label: "Leads CRM" },
  { to: "/ai",           icon: Sparkles,       label: "AI Assist", roles: ["admin","doctor"] },
  { to: "/staff",        icon: ShieldCheck,    label: "Staff & users", roles: ["admin"] },
];

/**
 * Inner sidebar content. Used by both the desktop Sidebar shell and the
 * mobile Sheet drawer in DashboardLayout. Accepts an optional `onNavigate`
 * callback so mobile drawer can close itself when a link is tapped.
 */
export function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const role = user?.role;
  const items = NAV.filter((n) => !n.roles || n.roles.includes(role));

  return (
    <div className="h-full flex flex-col" data-testid="sidebar-content">
      <div className="px-5 py-6 flex items-center gap-3">
        <img
          src={CLINIC.logoUrl}
          alt={CLINIC.name}
          className="w-11 h-11 rounded-full object-cover ring-1 ring-[color:var(--primary-light)]"
        />
        <div>
          <p className="font-heading text-[14px] leading-[16px] font-medium text-[color:var(--ink)]">
            {CLINIC.shortName}
          </p>
          <p className="text-[10px] text-[color:var(--muted)] tracking-[0.18em] uppercase">
            Homoeopathy
          </p>
        </div>
      </div>

      <nav className="px-3 flex-1 space-y-1 pt-2 overflow-y-auto">
        {items.map(({ to, icon: Icon, label }) => {
          const active =
            loc.pathname === to || loc.pathname.startsWith(to + "/");
          return (
            <Link
              key={to}
              to={to}
              onClick={onNavigate}
              className={`side-link ${active ? "active" : ""}`}
              data-testid={`sidebar-link-${label.toLowerCase().replace(/\s|&/g, "-").replace(/-+/g, "-")}`}
            >
              <Icon className="w-[18px] h-[18px]" strokeWidth={1.6} />
              <span>{label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-[color:var(--border)] space-y-2.5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[color:var(--primary-light)] grid place-items-center text-[color:var(--primary-hover)] font-medium">
            {user?.name?.[0] || "U"}
          </div>
          <div className="min-w-0">
            <p
              className="text-sm font-medium text-[color:var(--ink)] truncate"
              data-testid="sidebar-user-name"
            >
              {user?.name}
            </p>
            <p className="text-xs text-[color:var(--muted)] capitalize">
              {user?.role}
            </p>
          </div>
        </div>
        <ChangePasswordDialog>
          <button
            className="w-full flex items-center gap-2 text-sm text-[color:var(--muted)] hover:text-[color:var(--ink)]"
            data-testid="sidebar-change-password-btn"
          >
            <KeyRound className="w-4 h-4" strokeWidth={1.6} /> Change password
          </button>
        </ChangePasswordDialog>
        <button
          onClick={logout}
          className="w-full flex items-center gap-2 text-sm text-[color:var(--muted)] hover:text-[color:var(--ink)]"
          data-testid="sidebar-logout-btn"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.6} /> Logout
        </button>
      </div>
    </div>
  );
}

export default function Sidebar() {
  return (
    <aside
      className="hidden md:flex md:flex-col md:w-64 shrink-0 bg-white border-r border-[color:var(--border)]"
      data-testid="sidebar"
    >
      <SidebarContent />
    </aside>
  );
}
